// Talking to our own API. Every call is same-origin with the session cookie; errors carry the server's message.
import type { Preferences } from '../../shared/preferences'
import type {
  CalendarStyle,
  HomeStyle,
  MomentStyle,
  PeopleStyle,
  PhotoStyle,
  RecipeStyle,
  Sections,
} from '../../shared/sections'
import type { Uses } from '../../shared/uses'
import type { Palette } from '../../shared/palette'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
  }
}

export async function api<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method: init.method ?? 'GET',
    credentials: 'same-origin',
    headers: init.body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  })
  const data = (await res.json().catch(() => ({}))) as { error?: string }
  if (!res.ok) throw new ApiError(res.status, data.error ?? 'Something went wrong. Please try again.')
  return data as T
}

export type Me = {
  id: string
  name: string
  email: string
  admin: boolean
  // Set while the owner views the app as a test person: who they really are.
  viewAs?: { name: string } | null
  preferences: Preferences
  // What they use each section for, themselves (missing from an older server: everything).
  uses?: Uses
  portrait: string | null
  // Whether that face is their own profile photo, rather than a tree portrait or none.
  photo?: boolean
  // Whether anyone shares a circle with them or is connected to them, which is when Everyone means more than just them
  // (missing from an older server: yes).
  connected?: boolean
  // Whether Everyone is offered: they're in a circle or connected with someone, even if no one has joined yet, since
  // what's shared with Everyone reaches whoever joins later (missing from an older server: yes).
  everyone?: boolean
  // Who they last shared a new thing with, so the next one starts there (null before the first; missing from an older
  // server).
  lastShare?: { everyone: boolean; spaceIds: string[] } | null
  // Connected one to one but in no circle with anyone, so Everyone came from a connection.
  everyoneByConnection?: boolean
  // Someone is asking to connect with them.
  requests?: boolean
  // Whether "Capture recipe details from image" is switched on.
  cardReading?: boolean
  // Whether a moment can be polished or retold here.
  telling?: boolean
  // The owner's switches: which sections are on, and whether this person may send circle invites.
  sections: Sections
  // How Home is laid out for everyone (missing from an older server: Traditional).
  homeStyle?: HomeStyle
  // How the owner has Photos laid out for everyone; missing from an app that loaded before there was a choice.
  photosStyle?: PhotoStyle
  // How Moments is laid out for everyone (missing from an older server: Traditional).
  momentsStyle?: MomentStyle
  // How Recipes is laid out for everyone (missing from an older server: Traditional).
  recipesStyle?: RecipeStyle
  // How the Calendar is laid out for everyone (missing from an older server: Traditional).
  calendarStyle?: CalendarStyle
  // How People is laid out for everyone (missing from an older server: Traditional).
  peopleStyle?: PeopleStyle
  // The owner's colours over the theme (missing from an older server: none).
  palette?: Palette
  canInvite: boolean
}
